/**
 * The chevron dropdown on a signed-container row — an inline peek at what the
 * container encloses, without opening the signature panel.
 *
 * Shared by all three views (Folders, List, Timeline) so a container row reads
 * identically wherever it appears. Selecting an entry opens the signature panel
 * on that document; the panel is where the open/download actions live.
 *
 * The menu opens to the LEFT (`align="end"`): the chevron sits at the end of a
 * title row, and a right-aligned menu would run off the row and squeeze the
 * document names it exists to show.
 */

import { useState } from "react";
import { ChevronDown, Loader2 } from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { FileTypeIcon } from "@/components/ui/documents/file-type-icon";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { cn } from "@/lib/utils";
import { formatFileSize } from "@/utils/formatFileSize";
import { InContainerChip } from "../atoms/SignedContainerIndicators";
import type { EnclosedDocument } from "../../signing/signatureModel";
import { useEnclosedDocumentsQuery } from "../../signing/useSignedContainerInfo";
import { useSignaturePanel } from "../../signing/signaturePanelContext";

interface EnclosedDocumentsMenuProps {
  /** Id of the signed container whose contents the menu lists. */
  containerId: string;
  /** Container filename — names the format on each entry's "in .asice" chip. */
  containerFilename: string;
  className?: string;
}

export function EnclosedDocumentsMenu({
  containerId,
  containerFilename,
  className,
}: EnclosedDocumentsMenuProps) {
  const { t } = useLanguage();
  const tp = t.documents.signedContainer.panel;
  const [wasOpened, setWasOpened] = useState(false);
  const { data, isPending, isError } = useEnclosedDocumentsQuery(containerId, wasOpened);
  const enclosed = data ?? [];

  return (
    <DropdownMenu onOpenChange={(open) => setWasOpened(wasOpened || open)}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={t.documents.signedContainer.showEnclosed}
          onClick={(e) => e.stopPropagation()}
          className={cn(
            "flex-shrink-0 rounded p-0.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground",
            className
          )}
        >
          <ChevronDown className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      {/* w-80 clipped every entry name on real containers (a 33-char drawing
          filename needs ~270px, w-80 left 151px); 28rem still left the longest
          of them 3px short, so 30rem — and the viewport cap keeps the menu on
          screen on a phone. */}
      <DropdownMenuContent
        align="end"
        className="max-h-80 w-[30rem] max-w-[calc(100vw-2rem)] overflow-y-auto"
      >
        {isPending && (
          <DropdownMenuItem disabled>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            {tp.loading}
          </DropdownMenuItem>
        )}
        {isError && (
          <DropdownMenuItem disabled className="text-destructive">
            {tp.enclosedError}
          </DropdownMenuItem>
        )}
        {!isPending && !isError && enclosed.length === 0 && (
          <DropdownMenuItem disabled>{tp.enclosedEmpty}</DropdownMenuItem>
        )}
        {enclosed.map((doc) => (
          <EnclosedMenuItem
            key={doc.id}
            containerId={containerId}
            containerFilename={containerFilename}
            doc={doc}
          />
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function EnclosedMenuItem({
  containerId,
  containerFilename,
  doc,
}: {
  containerId: string;
  containerFilename: string;
  doc: EnclosedDocument;
}) {
  const panel = useSignaturePanel();

  return (
    <DropdownMenuItem
      className="gap-3"
      onSelect={() =>
        panel?.open({
          containerId,
          enclosedDocumentId: doc.id,
          enclosedFilename: doc.filename,
        })
      }
    >
      <FileTypeIcon filename={doc.filename} />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium">{doc.filename}</span>
        <span className="block text-xs text-muted-foreground">{formatFileSize(doc.size ?? 0)}</span>
      </span>
      {/* The entry is a document INSIDE the container, so it states that — the
          container's own signature status belongs on the container row. */}
      <InContainerChip containerFilename={containerFilename} />
    </DropdownMenuItem>
  );
}
